import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Building, Users, TrendingUp, BarChart3, Plus, Eye, Edit, Trash2,
  CheckCircle, AlertCircle, Crown, CreditCard, Send, FileText,
  Target, Zap, MessageCircle, Shield, Award, MapPin, DollarSign,
  ChevronRight, RefreshCw, Download, Printer, X, Heart, Home, Activity,
  Upload, Image as ImageIcon, Bed, Bath, Square, User, Clock, Calculator, Map
} from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Funnel, FunnelChart, LabelList } from "recharts";
import { VibeTag, getAllVibeTagsArray, VIBE_TAGS } from "@/components/VibeTag";
import { RICReport } from "@/components/RICReport";
import ImageUploader from "@/components/ImageUploader";
import { MembershipBanner } from "@/components/MembershipBanner";
import { PropertyDetailModal, PropertyCard } from "@/components/PropertyDetailModal";
import { PhotoManager } from "@/components/PhotoManager";
import ValuationModule from "@/components/ValuationModule";
import TerrenoModule from "@/components/TerrenoModule";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

import { MEXICAN_CITIES as MUNICIPALITIES } from "@/constants/mexicanCities";

// Membership Plans - Updated pricing
const PLANS = {
  freemium: { name: "Prueba Gratuita (7 días)", price: 0, properties: 3, days: 7, description: "Acceso gratuito por 7 días" },
  monthly: { name: "Mensual", price: 1970, properties: 50, days: 30, description: "Hasta 50 propiedades por mes" },
  semester: { name: "Semestral", price: 11820, properties: 70, days: 180, description: "Hasta 70 propiedades por 6 meses" },
  annual: { name: "Anual", price: 23640, properties: 100, days: 365, description: "Hasta 100 propiedades por año" }
};

// Radar Chart Colors
const RADAR_COLORS = {
  property: "#ccd500",
  buyer: "#384451"
};

// Property Wizard Component
const PropertyWizard = ({ onComplete, onCancel, existingProperty }) => {
  const [currentWave, setCurrentWave] = useState(1);
  const [formData, setFormData] = useState(existingProperty || {
    title: "",
    story: "",
    images: [],  // Changed from [""] to support ImageUploader
    municipality: "",
    address: "",
    coordinates: { lat: 19.4326, lng: -99.1332 },
    features: { bedrooms: 2, bathrooms: 2, land_area_m2: 100, construction_area_m2: 80, price: 3000000 },
    psychographic_attributes: { emotional: {}, technical: {}, urban: {}, financial: {} },
    owner_name: "",
    owner_contact: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);  // For ImageUploader

  const waves = [
    { id: 1, name: "ADN Emocional", category: "emotional", questions: 15, icon: Heart },
    { id: 2, name: "Pilar Constructivo", category: "technical", questions: 12, icon: Building },
    { id: 3, name: "Contexto Urbano", category: "urban", questions: 10, icon: MapPin },
    { id: 4, name: "Viabilidad Financiera", category: "financial", questions: 15, icon: DollarSign }
  ];

  const questions = {
    emotional: [
      { id: "e1", text: "¿Cómo entra la luz natural?", type: "choice", options: ["Amante del amanecer ☀️", "Fan del atardecer 🌅", "Refugio en sombra 🌙"] },
      { id: "e2", text: "Nivel de ruido urbano de la zona:", type: "scale", min: 1, max: 10 },
      { id: "e3", text: "¿Tiene espacio para ritual del café?", type: "choice", options: ["Sí, necesito mi estación de café ☕", "Un rincón desayunador con vista", "No es importante"] },
      { id: "e6", text: "¿Tiene rincón de lectura?", type: "boolean" },
      { id: "e7", text: "¿Tiene espacio de meditación/yoga?", type: "boolean" },
      { id: "e8", text: "¿La cocina es social?", type: "choice", options: ["Sí, para convivir 👨‍👩‍👧", "Es un área técnica cerrada", "Término medio"] },
      { id: "e10", text: "Conexión con naturaleza:", type: "scale", min: 1, max: 10 },
      { id: "e11", text: "¿Es pet-friendly?", type: "choice", options: ["Sí, perro 🐕", "Sí, gato 🐱", "Otras mascotas", "No"] },
      { id: "e14", text: "Tipo de techos:", type: "choice", options: ["Libre y creativo 🕊️", "Estándar", "Acogedores bajos"] },
      { id: "e15", text: "¿Tiene un 'Wow Factor'?", type: "choice", options: ["Sí, algo único e impactante ✨", "No es prioridad", "Prefiero funcionalidad"] }
    ],
    technical: [
      { id: "t3", text: "Espacio para home office:", type: "choice", options: ["Espacio diseñado específico 💼", "Recámara adaptable", "No aplica"] },
      { id: "t4", text: "Ventilación:", type: "choice", options: ["Natural todo el año 🌬️", "Depende de A/C", "Combinación"] },
      { id: "t5", text: "Aislamiento acústico entre cuartos:", type: "scale", min: 1, max: 10 },
      { id: "t7", text: "¿Tiene cargador para auto eléctrico?", type: "choice", options: ["Ya tiene uno 🔌", "Puede instalarse", "No"] },
      { id: "t9", text: "Eficiencia energética:", type: "choice", options: ["Paneles solares esenciales ☀️", "Calentador solar", "No tiene"] },
      { id: "t11", text: "Tipo de iluminación:", type: "choice", options: ["Luz cálida foseada 💡", "Focos tradicionales", "Combinación"] },
      { id: "t12", text: "Seguridad tecnológica:", type: "choice", options: ["Cámaras y alarmas 📹", "Básica", "No tiene"] }
    ],
    urban: [
      { id: "u1", text: "Distancia a cafeterías:", type: "choice", options: ["A la vuelta de la esquina ☕", "Un paseo tranquilo (5-10 min)", "Hay que manejar"] },
      { id: "u2", text: "Seguridad del vecindario:", type: "scale", min: 1, max: 10 },
      { id: "u8", text: "Distancia a parques pet-friendly:", type: "choice", options: ["Menos de 5 min caminando 🐕", "10-15 min", "No aplica"] },
      { id: "u9", text: "Oferta gastronómica cercana:", type: "scale", min: 1, max: 10 },
      { id: "u10", text: "Calidad de internet:", type: "choice", options: ["Fibra óptica esencial 🌐", "Internet estándar", "Básico"] }
    ],
    financial: [
      { id: "f1", text: "Rango de precio (MXN):", type: "choice", options: ["0-2M", "2-5M", "5-10M", "10M+"] },
      { id: "f2", text: "Mantenimiento mensual estimado:", type: "choice", options: ["Menos de 5K", "5-10K", "10-20K", "20K+"] },
      { id: "f4", text: "Mejor uso:", type: "choice", options: ["Inversión (ROI) 📈", "Vivienda principal 🏠", "Ambos"] },
      { id: "f7", text: "¿Apto para Airbnb?", type: "choice", options: ["Sí, es parte del plan", "Tal vez", "No permitido"] },
      { id: "f8", text: "Estado de la propiedad:", type: "choice", options: ["Lista para habitar ✅", "Necesita mejoras menores", "Requiere remodelación"] }
    ]
  };

  const updateAttribute = (category, questionId, value) => {
    setFormData(prev => ({
      ...prev,
      psychographic_attributes: {
        ...prev.psychographic_attributes,
        [category]: {
          ...prev.psychographic_attributes[category],
          [questionId]: value
        }
      }
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/agent/properties`, formData);
      toast.success("Propiedad creada exitosamente");
      onComplete(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear propiedad");
    } finally {
      setSaving(false);
    }
  };

  const currentCategory = waves[currentWave - 1]?.category;
  const currentQuestions = questions[currentCategory] || [];

  return (
    <div className="space-y-6">
      {/* Wave Progress */}
      <div className="flex items-center justify-between mb-6">
        {waves.map((wave, idx) => (
          <div key={wave.id} className="flex items-center">
            <button
              onClick={() => setCurrentWave(wave.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                currentWave === wave.id 
                  ? "bg-primary text-[#384451] font-medium" 
                  : currentWave > wave.id 
                    ? "bg-green-100 text-green-700" 
                    : "bg-slate-100 text-slate-500"
              }`}
            >
              {currentWave > wave.id ? <CheckCircle className="w-4 h-4" /> : <wave.icon className="w-4 h-4" />}
              <span className="hidden md:inline">{wave.name}</span>
              <span className="md:hidden">Ola {wave.id}</span>
            </button>
            {idx < waves.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 mx-2" />}
          </div>
        ))}
      </div>

      {/* Wave 0: Basic Info */}
      {currentWave === 1 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título de la Propiedad</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ej: Loft Industrial en Roma Norte"
                />
              </div>
              <div>
                <Label>Municipio/Colonia</Label>
                <Select value={formData.municipality} onValueChange={(v) => setFormData({...formData, municipality: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    {MUNICIPALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Historia/Descripción Emocional</Label>
              <Textarea 
                value={formData.story}
                onChange={(e) => setFormData({...formData, story: e.target.value})}
                placeholder="Describe la propiedad de forma que conecte emocionalmente..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Recámaras</Label>
                <Input type="number" value={formData.features.bedrooms} onChange={(e) => setFormData({...formData, features: {...formData.features, bedrooms: parseInt(e.target.value)}})} />
              </div>
              <div>
                <Label>Baños</Label>
                <Input type="number" step="0.5" value={formData.features.bathrooms} onChange={(e) => setFormData({...formData, features: {...formData.features, bathrooms: parseFloat(e.target.value)}})} />
              </div>
              <div>
                <Label>Superficie Terreno (m²)</Label>
                <Input 
                  type="number" 
                  value={formData.features.land_area_m2} 
                  onChange={(e) => setFormData({...formData, features: {...formData.features, land_area_m2: parseInt(e.target.value)}})} 
                  placeholder="Área total del terreno"
                  data-testid="land-area-input"
                />
              </div>
              <div>
                <Label>Superficie Construida (m²)</Label>
                <Input 
                  type="number" 
                  value={formData.features.construction_area_m2} 
                  onChange={(e) => setFormData({...formData, features: {...formData.features, construction_area_m2: parseInt(e.target.value)}})} 
                  placeholder="Área de construcción"
                  data-testid="construction-area-input"
                />
              </div>
              <div>
                <Label>Precio (MXN)</Label>
                <Input type="number" value={formData.features.price} onChange={(e) => setFormData({...formData, features: {...formData.features, price: parseInt(e.target.value)}})} />
              </div>
            </div>
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Fotografías de la Propiedad
              </Label>
              <ImageUploader
                images={uploadedImages}
                onChange={(newImages) => {
                  setUploadedImages(newImages);
                  // Extract URLs/dataUrls for the form
                  const imageUrls = newImages.map(img => img.dataUrl || img);
                  setFormData({...formData, images: imageUrls});
                }}
                maxImages={10}
                maxSizeKB={500}
              />
            </div>
            
            <div>
              <Label>Dirección Completa (Confidencial)</Label>
              <Input 
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Se revela solo tras firma de contrato"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Psychographic Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Ola {currentWave}: {waves[currentWave - 1]?.name || ""}
          </CardTitle>
          <CardDescription>
            Responde estas preguntas para crear el perfil psicográfico de la propiedad
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {currentQuestions.map(q => (
            <div key={q.id} className="space-y-2">
              <Label>{q.text}</Label>
              {q.type === "choice" && (
                <div className="flex flex-wrap gap-2">
                  {q.options.map(opt => (
                    <Button
                      key={opt}
                      variant={formData.psychographic_attributes[currentCategory]?.[q.id] === opt ? "default" : "outline"}
                      size="sm"
                      onClick={() => updateAttribute(currentCategory, q.id, opt)}
                      className={formData.psychographic_attributes[currentCategory]?.[q.id] === opt ? "bg-primary text-[#384451]" : ""}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              )}
              {q.type === "scale" && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">{q.min}</span>
                  <Slider
                    value={[formData.psychographic_attributes[currentCategory]?.[q.id] || 5]}
                    onValueChange={([v]) => updateAttribute(currentCategory, q.id, v)}
                    min={q.min}
                    max={q.max}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm text-slate-500">{q.max}</span>
                  <Badge variant="secondary">{formData.psychographic_attributes[currentCategory]?.[q.id] || 5}</Badge>
                </div>
              )}
              {q.type === "boolean" && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.psychographic_attributes[currentCategory]?.[q.id] || false}
                    onCheckedChange={(v) => updateAttribute(currentCategory, q.id, v)}
                  />
                  <span className="text-sm">{formData.psychographic_attributes[currentCategory]?.[q.id] ? "Sí" : "No"}</span>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={currentWave === 1 ? onCancel : () => setCurrentWave(currentWave - 1)}>
          {currentWave === 1 ? "Cancelar" : "Anterior"}
        </Button>
        {currentWave < 4 ? (
          <Button onClick={() => setCurrentWave(currentWave + 1)} className="bg-primary text-[#384451]">
            Siguiente Ola <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving} className="bg-primary text-[#384451]">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Publicar Propiedad
          </Button>
        )}
      </div>
    </div>
  );
};

// Match Gap Analysis Component
const MatchGapAnalysis = ({ propertyId, onEditProperty }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`${API}/agent/match-gap/${propertyId}`);
        setAnalysis(response.data);
      } catch (error) {
        console.error("Error fetching match gap:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [propertyId]);

  if (loading) return <div className="flex items-center justify-center p-8"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
  if (!analysis) return <div className="text-center p-8 text-slate-500">No hay datos suficientes para analizar</div>;

  const chartData = Object.entries(analysis.failure_analysis).map(([cat, data]) => ({
    category: cat === "emotional" ? "Emocional" : cat === "technical" ? "Técnico" : cat === "urban" ? "Urbano" : "Financiero",
    failure_rate: data.failure_rate,
    success_rate: 100 - data.failure_rate
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Análisis de Brecha: {analysis.property_title}</h3>
        <Badge variant="secondary">{analysis.total_swipes} interacciones analizadas</Badge>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Tasa de Fallo por Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="category" type="category" width={80} />
                <Tooltip />
                <Bar dataKey="failure_rate" fill="#ef4444" name="% Fallo" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Sugerencias de Mejora</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analysis.suggestions.map((sug, idx) => {
                const haystack = `${sug.category || ""} ${sug.issue || ""} ${sug.suggestion || ""}`.toLowerCase();
                let tab = "info";
                if (haystack.includes("foto") || haystack.includes("imagen")) tab = "photos";
                else if (haystack.includes("psicograf") || haystack.includes("ola") || haystack.includes("factor") || haystack.includes("emocional") || haystack.includes("urbano") || haystack.includes("vibe")) tab = "psychographic";
                return (
                  <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-amber-800">{sug.category}</span>
                    </div>
                    <p className="text-sm text-amber-700">{sug.issue}</p>
                    <p className="text-sm text-amber-900 mt-2 font-medium">{sug.suggestion}</p>
                    {onEditProperty && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-7 px-0 text-amber-900 font-semibold"
                        onClick={() => onEditProperty(propertyId, tab)}
                        data-testid={`matchgap-edit-${idx}`}
                      >
                        Ir a la ficha y editar <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {analysis.suggestions.length === 0 && (
                <div className="text-center py-4 text-green-600">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                  <p>¡La propiedad tiene buen desempeño en todas las categorías!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Presentation Mode Component
const PresentationMode = ({ propertyId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/agent/presentation/${propertyId}`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching presentation:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [propertyId]);

  const handlePrint = () => window.print();

  if (loading) return <div className="flex items-center justify-center p-12"><RefreshCw className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!data) return null;

  const radarData = [
    { category: "Emocional", score: data.psychographic_performance.emotional, fullMark: 100 },
    { category: "Técnico", score: data.psychographic_performance.technical, fullMark: 100 },
    { category: "Urbano", score: data.psychographic_performance.urban, fullMark: 100 },
    { category: "Financiero", score: data.psychographic_performance.financial, fullMark: 100 }
  ];

  return (
    <div className="print:p-8" id="presentation-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 print:mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#384451]">Reporte de Desempeño Psicográfico</h1>
          <p className="text-slate-500">Generado por {data.agent_name} • {new Date(data.report_date).toLocaleDateString()}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" /> Imprimir
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Property Info */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold">{data.property.title}</h2>
              <p className="text-slate-500 flex items-center gap-1"><MapPin className="w-4 h-4" /> {data.property.municipality}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">${(data.property.price / 1000000).toFixed(1)}M MXN</p>
              <p className="text-sm text-slate-500">Publicada hace {Math.floor((Date.now() - new Date(data.property.created_at).getTime()) / (1000 * 60 * 60 * 24))} días</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Eye className="w-6 h-6 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{data.performance.views}</p>
            <p className="text-xs text-slate-500">Visualizaciones</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Heart className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{data.performance.right_swipes}</p>
            <p className="text-xs text-slate-500">Likes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Target className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{data.performance.total_matches}</p>
            <p className="text-xs text-slate-500">Matches</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold">{data.performance.contracts_signed}</p>
            <p className="text-xs text-slate-500">Contratos</p>
          </CardContent>
        </Card>
      </div>

      {/* Psychographic Performance */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>Desempeño por Pilar Psicográfico</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="category" />
                <PolarRadiusAxis domain={[0, 100]} />
                <Radar name="Puntuación" dataKey="score" stroke="#ccd500" fill="#ccd500" fillOpacity={0.5} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Match Score Promedio</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center h-[250px]">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="64" cy="64" r="56" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                <circle 
                  cx="64" cy="64" r="56" 
                  stroke="#ccd500" 
                  strokeWidth="12" 
                  fill="none"
                  strokeDasharray={`${(data.performance.avg_match_score / 100) * 352} 352`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-[#384451]">{data.performance.avg_match_score}%</span>
              </div>
            </div>
            <p className="mt-4 text-slate-500">Compatibilidad promedio con compradores</p>
          </CardContent>
        </Card>
      </div>

      {/* Vibe Performance */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Rendimiento de Vibe Tags</CardTitle>
          <CardDescription>Qué tanto resonaron los tags de la propiedad con los compradores</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.vibe_performance).map(([tagId, perf]) => {
              const tag = getAllVibeTagsArray().find(t => t.id === tagId);
              const matchRate = perf.total > 0 ? Math.round((perf.matched / perf.total) * 100) : 0;
              return tag ? (
                <div key={tagId} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <VibeTag tag={tag} size="sm" />
                  <span className={`text-sm font-medium ${matchRate >= 70 ? "text-green-600" : matchRate >= 40 ? "text-amber-600" : "text-red-600"}`}>
                    {matchRate}%
                  </span>
                </div>
              ) : null;
            })}
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-slate-400 mt-8">
        <p>Reporte generado por Findperty • www.findperty.mx</p>
        <p>Powered by 52-Point Psychographic Matching Engine</p>
      </div>
    </div>
  );
};

// Main Agent Dashboard Component
export const AgentDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showMembership, setShowMembership] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPresentation, setShowPresentation] = useState(false);
  const [showMatchGap, setShowMatchGap] = useState(false);
  const [showRIC, setShowRIC] = useState(false);
  
  // New states for image upload and property editing
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editPropertyData, setEditPropertyData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef(null);
  
  // State for property detail modal
  const [showPropertyDetailModal, setShowPropertyDetailModal] = useState(false);
  const [propertyDetailInitialEditMode, setPropertyDetailInitialEditMode] = useState(false);
  const [propertyDetailInitialEditTab, setPropertyDetailInitialEditTab] = useState("info");

  // Helper triggered by RIC tips or MatchGap suggestions to jump into property edit
  const openPropertyEdit = async (propId, tab = "info") => {
    try {
      const response = await axios.get(`${API}/agent/properties/${propId}`);
      setPropertyDetailData({ property: response.data, statistics: null });
      setPropertyDetailInitialEditMode(true);
      setPropertyDetailInitialEditTab(tab);
      setShowPropertyDetailModal(true);
      setShowRIC(false);
      setShowMatchGap(false);
    } catch (error) {
      toast.error("No se pudo abrir la ficha para edición");
    }
  };
  const [propertyDetailData, setPropertyDetailData] = useState(null);

  // Get membership info from auth context
  const { membership, checkMembershipStatus } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, propertiesRes, leadsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/agent/profile`),
        axios.get(`${API}/agent/properties`),
        axios.get(`${API}/agent/leads`),
        axios.get(`${API}/agent/analytics`)
      ]);
      
      setProfile(profileRes.data);
      setProperties(propertiesRes.data);
      setLeads(leadsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching agent data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMembershipPurchase = async (plan, paymentMethod) => {
    try {
      const response = await axios.post(`${API}/agent/membership/checkout`, null, {
        params: { plan, payment_method: paymentMethod, origin_url: window.location.origin }
      });
      
      if (paymentMethod === "stripe") {
        window.location.href = response.data.url;
      } else {
        toast.success("Transferencia registrada. Espera la verificación del administrador.");
        setShowMembership(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar");
    }
  };

  // Image upload function
  const handleImageUpload = async (file) => {
    if (!file || !selectedProperty) return;
    
    // Validate file size (10MB max before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 10MB antes de compresión.");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/agent/properties/${selectedProperty}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.compressed) {
        toast.success(`Imagen comprimida de ${response.data.original_size_kb}KB a ${response.data.final_size_kb}KB`);
      } else {
        toast.success("Imagen subida exitosamente");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingImage(false);
    }
  };

  // Delete image function
  const handleDeleteImage = async (propertyId, imageIndex) => {
    try {
      await axios.delete(`${API}/agent/properties/${propertyId}/images/${imageIndex}`);
      toast.success("Imagen eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar imagen");
    }
  };

  // Open edit property modal
  const openEditProperty = (prop) => {
    setSelectedProperty(prop.id);
    setEditPropertyData({
      title: prop.title || "",
      story: prop.story || "",
      municipality: prop.municipality || "",
      features: prop.features || { bedrooms: 2, bathrooms: 2, land_area_m2: 100, construction_area_m2: 80, price: 3000000 }
    });
    setShowEditProperty(true);
  };

  // Save property edits
  const savePropertyEdits = async () => {
    if (!selectedProperty || !editPropertyData) return;
    
    setSavingEdit(true);
    try {
      await axios.put(`${API}/agent/properties/${selectedProperty}`, editPropertyData);
      toast.success("Propiedad actualizada exitosamente");
      setShowEditProperty(false);
      setSelectedProperty(null);
      setEditPropertyData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar propiedad");
    } finally {
      setSavingEdit(false);
    }
  };

  // Delete property function
  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta propiedad?")) return;
    
    try {
      await axios.delete(`${API}/agent/properties/${propertyId}`);
      toast.success("Propiedad eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar propiedad");
    }
  };

  // View property full details
  const viewPropertyDetail = (prop) => {
    setPropertyDetailData({
      property: prop,
      statistics: {
        views: prop.views || 0,
        total_swipes: (prop.swipes_right || 0) + (prop.swipes_left || 0),
        total_matches: prop.match_count || 0
      }
    });
    setShowPropertyDetailModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <RefreshCw className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  // Presentation Mode
  if (showPresentation && selectedProperty) {
    return (
      <div className="min-h-screen bg-white p-8">
        <PresentationMode propertyId={selectedProperty} onClose={() => { setShowPresentation(false); setSelectedProperty(null); }} />
      </div>
    );
  }

  // Property Wizard Mode
  if (showWizard) {
    return (
      <div className="min-h-screen bg-slate-100 p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-[#384451] mb-6">Nueva Propiedad - Wizard de 4 Olas</h1>
          <PropertyWizard 
            onComplete={() => { setShowWizard(false); fetchData(); }} 
            onCancel={() => setShowWizard(false)} 
          />
        </div>
      </div>
    );
  }

  const funnelData = analytics?.conversion_funnel ? [
    { name: "Matches", value: analytics.conversion_funnel.matches, fill: "#ccd500" },
    { name: "Contratos", value: analytics.conversion_funnel.contracts_generated, fill: "#94a3b8" },
    { name: "Firmados", value: analytics.conversion_funnel.contracts_signed, fill: "#22c55e" },
    { name: "Visitas", value: analytics.conversion_funnel.visits_completed, fill: "#3b82f6" }
  ] : [];

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white p-6 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-primary">Findperty</h1>
          <p className="text-sm text-slate-400">Portal de Agente</p>
        </div>
        
        {/* Profile Summary */}
        <div className="mb-6 p-4 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            {profile?.verified ? (
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
            )}
            <div>
              <p className="font-medium text-sm">{profile?.agency_name || "Mi Agencia"}</p>
              {profile?.verified && <Badge className="bg-green-500 text-white text-xs">Verificado</Badge>}
            </div>
          </div>
          <div className="text-xs text-slate-400 space-y-1">
            <p>Plan: <span className="text-primary font-medium">{PLANS[profile?.membership_plan]?.name || "Freemium"}</span></p>
            <p>Propiedades: {profile?.property_count || 0} / {profile?.property_limit || 3}</p>
          </div>
          <Progress value={(profile?.property_count / profile?.property_limit) * 100} className="mt-2 h-1.5" />
        </div>
        
        <nav className="space-y-2 flex-1">
          {[
            { id: "overview", label: "Resumen", icon: BarChart3 },
            { id: "properties", label: "Mis Propiedades", icon: Building },
            { id: "leads", label: "Leads Activos", icon: Target },
            { id: "valuation", label: "Valuación MVIP", icon: Calculator },
            { id: "terreno", label: "Potencial Terreno", icon: Map },
            { id: "analytics", label: "Analytics", icon: TrendingUp },
            { id: "profile", label: "Mi Perfil", icon: Users }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id ? "bg-primary text-slate-900 font-medium" : "text-slate-300 hover:bg-slate-800"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        
        <div className="pt-4 border-t border-slate-700 space-y-2">
          <Button 
            className="w-full bg-primary text-slate-900 hover:bg-[#b8c100]"
            onClick={() => {
              if ((profile?.property_count || 0) >= (profile?.property_limit || 3)) {
                setShowMembership(true);
              } else {
                setShowWizard(true);
              }
            }}
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva Propiedad
          </Button>
          <Button variant="outline" className="w-full text-slate-300 border-slate-700 hover:bg-slate-800" onClick={() => setShowMembership(true)}>
            <Crown className="w-4 h-4 mr-2" /> Membresía
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Membership Banner - hidden on Valuación and Terreno tabs */}
        {activeTab !== "valuation" && activeTab !== "terreno" && (
          <MembershipBanner membership={membership} onUpgrade={() => setShowMembership(true)} />
        )}

        {/* Profile Completion CTA — visible when core fields are missing */}
        {activeTab !== "profile" && profile && (!profile.bio || !profile.phone || !profile.municipality || !profile.rfc || !profile.agent_photo) && (
          <div className="mb-4 rounded-xl bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/50 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="complete-profile-cta">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#384451]" />
              <div>
                <p className="font-bold text-[#384451]">Completa tu perfil de agente</p>
                <p className="text-xs text-slate-600">Sube tu foto, RFC, bio y territorio para verificarte y aparecer ante compradores.</p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-primary text-[#384451] hover:bg-[#b8c100] font-bold"
              onClick={() => setActiveTab("profile")}
              data-testid="go-to-profile-btn"
            >
              Completar perfil <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
        
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#384451]">Bienvenido de vuelta</h2>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Propiedades</p>
                      <p className="text-2xl font-bold">{properties.length}</p>
                    </div>
                    <Building className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Leads Activos</p>
                      <p className="text-2xl font-bold">{leads.length}</p>
                    </div>
                    <Target className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Tasa de Swipe</p>
                      <p className="text-2xl font-bold">{analytics?.visibility?.swipe_rate || 0}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Match Promedio</p>
                      <p className="text-2xl font-bold">{Math.round(analytics?.avg_match_score || 0)}%</p>
                    </div>
                    <Heart className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle>Embudo de Conversión</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-around">
                  {funnelData.map((stage, idx) => (
                    <div key={stage.name} className="flex items-center">
                      <div className="text-center">
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: stage.fill + "20", border: `3px solid ${stage.fill}` }}>
                          <span className="text-xl font-bold">{stage.value}</span>
                        </div>
                        <p className="text-sm text-slate-600">{stage.name}</p>
                      </div>
                      {idx < funnelData.length - 1 && <ChevronRight className="w-6 h-6 text-slate-300 mx-4" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Demand Intelligence */}
            {analytics?.demand_intelligence?.top_demanded && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" /> What's Hot en tu Zona
                  </CardTitle>
                  <CardDescription>Lo que más buscan los compradores</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {analytics.demand_intelligence.top_demanded.slice(0, 10).map(([tagId, count]) => {
                      const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                      return tag ? (
                        <div key={tagId} className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg">
                          <VibeTag tag={tag} size="sm" />
                          <span className="text-sm font-bold text-primary">{count}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === "properties" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#384451]">Mis Propiedades</h2>
              <Button onClick={() => setShowWizard(true)} className="bg-primary text-[#384451]" data-testid="new-property-btn">
                <Plus className="w-4 h-4 mr-2" /> Nueva Propiedad
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {properties.map(prop => (
                <Card 
                  key={prop.id} 
                  className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" 
                  data-testid={`property-card-${prop.id}`}
                  onClick={() => viewPropertyDetail(prop)}
                >
                  <div className="flex">
                    <div className="relative">
                      <img src={prop.images?.[0] || "https://via.placeholder.com/200"} alt={prop.title} className="w-40 h-40 object-cover" />
                      <div className="absolute bottom-2 right-2 flex gap-1">
                        <Button 
                          size="sm" 
                          variant="secondary" 
                          className="h-7 px-2 bg-white/90 hover:bg-white"
                          onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop.id); setShowImageUpload(true); }}
                          data-testid={`upload-img-${prop.id}`}
                        >
                          <Upload className="w-3 h-3 mr-1" /> {prop.images?.length || 0}
                        </Button>
                      </div>
                    </div>
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-lg mb-1">{prop.title}</h3>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); openEditProperty(prop); }}
                            data-testid={`edit-prop-${prop.id}`}
                          >
                            <Edit className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); handleDeleteProperty(prop.id); }}
                            data-testid={`delete-prop-${prop.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mb-2"><MapPin className="w-3 h-3 inline mr-1" />{prop.municipality}</p>
                      {/* Show property features */}
                      {prop.features && (
                        <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
                          {prop.features.bedrooms !== undefined && (
                            <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {prop.features.bedrooms}</span>
                          )}
                          {prop.features.bathrooms !== undefined && (
                            <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {prop.features.bathrooms}</span>
                          )}
                          {(prop.features.land_area_m2 || prop.features.area) && (
                            <span className="flex items-center gap-1" title="Terreno"><Square className="w-3 h-3" /> {prop.features.land_area_m2 || prop.features.area}m²</span>
                          )}
                          {prop.features.construction_area_m2 && (
                            <span className="flex items-center gap-1" title="Construido"><Home className="w-3 h-3" /> {prop.features.construction_area_m2}m²</span>
                          )}
                          {prop.features.price !== undefined && (
                            <span className="font-semibold text-primary">${(prop.features.price / 1000000).toFixed(1)}M</span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-1 mb-3 flex-wrap">
                        {prop.vibe_tags?.slice(0, 3).map(tagId => {
                          const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                          return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
                        })}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span><Eye className="w-4 h-4 inline mr-1" />{prop.views || 0}</span>
                        <span><Heart className="w-4 h-4 inline mr-1" />{prop.swipes_right || 0}</span>
                        <span><Target className="w-4 h-4 inline mr-1" />{prop.match_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-slate-50 flex justify-between items-center flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    <Badge variant={prop.status === "active" ? "default" : prop.status === "pending_approval" ? "secondary" : "outline"} 
                      className={prop.status === "active" ? "bg-green-500" : prop.status === "pending_approval" ? "bg-orange-500 text-white" : ""}>
                      {prop.status === "active" ? "Activa" : prop.status === "pending_approval" ? "Pendiente" : "Inactiva"}
                    </Badge>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        className="bg-primary text-[#384451] hover:bg-[#b8c100] font-semibold"
                        onClick={(e) => { e.stopPropagation(); openEditProperty(prop); }}
                        data-testid={`edit-btn-${prop.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Editar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop.id); setShowRIC(true); }} data-testid={`ric-btn-${prop.id}`}>
                        <Activity className="w-4 h-4" /> RIC
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop.id); setShowMatchGap(true); }}>
                        <AlertCircle className="w-4 h-4" /> Match Gap
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop.id); setShowPresentation(true); }}>
                        <FileText className="w-4 h-4" /> Reporte
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#384451]">Leads Activos (&gt;70% Compatible)</h2>

            {leads.map(lead => (
              <Card key={lead.id} className="p-6">
                <div className="flex gap-6">
                  {/* Property Image */}
                  <img src={lead.property?.images?.[0] || "https://via.placeholder.com/150"} alt="" className="w-32 h-32 object-cover rounded-lg" />
                  
                  {/* Lead Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{lead.property?.title}</h3>
                        <p className="text-sm text-slate-500">Comprador: {lead.shopper?.name}</p>
                      </div>
                      <Badge className="bg-primary text-[#384451] text-lg px-4 py-1">
                        {lead.match_score}% Match
                      </Badge>
                    </div>
                    
                    {/* Radar Chart */}
                    <div className="grid grid-cols-2 gap-4">
                      <ResponsiveContainer width="100%" height={150}>
                        <RadarChart data={[
                          { category: "Emocional", property: lead.breakdown?.emotional || 50, buyer: 100 },
                          { category: "Técnico", property: lead.breakdown?.technical || 50, buyer: 100 },
                          { category: "Urbano", property: lead.breakdown?.urban || 50, buyer: 100 },
                          { category: "Financiero", property: lead.breakdown?.financial || 50, buyer: 100 }
                        ]}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 100]} tick={false} />
                          <Radar name="Match" dataKey="property" stroke="#ccd500" fill="#ccd500" fillOpacity={0.5} />
                        </RadarChart>
                      </ResponsiveContainer>
                      
                      <div className="space-y-2">
                        {Object.entries(lead.breakdown || {}).map(([cat, score]) => (
                          <div key={cat} className="flex items-center gap-2">
                            <span className="text-xs w-20 capitalize">{cat === "emotional" ? "Emocional" : cat === "technical" ? "Técnico" : cat === "urban" ? "Urbano" : "Financiero"}</span>
                            <Progress value={score} className="flex-1 h-2" />
                            <span className="text-xs font-medium w-10">{Math.round(score)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button size="sm" className="bg-primary text-[#384451]">
                      <MessageCircle className="w-4 h-4 mr-1" /> Chat
                    </Button>
                    <Button size="sm" variant="outline">
                      <FileText className="w-4 h-4 mr-1" /> Contrato
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Valuation MVIP Tab */}
        {activeTab === "valuation" && (
          <ValuationModule />
        )}

        {/* Terreno Tab */}
        {activeTab === "terreno" && (
          <TerrenoModule />
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#384451]">Analytics & KPIs</h2>

            <div className="grid grid-cols-2 gap-6">
              {/* Visibility Index */}
              <Card>
                <CardHeader>
                  <CardTitle>Índice de Visibilidad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span>Visualizaciones Totales</span>
                      <span className="font-bold">{analytics?.visibility?.total_views || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Swipes a la Derecha</span>
                      <span className="font-bold text-green-600">{analytics?.visibility?.right_swipes || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Swipes a la Izquierda</span>
                      <span className="font-bold text-red-600">{analytics?.visibility?.left_swipes || 0}</span>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Tasa de Conversión</span>
                        <span className="text-2xl font-bold text-primary">{analytics?.visibility?.swipe_rate || 0}%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Match Density */}
              <Card>
                <CardHeader>
                  <CardTitle>Densidad de Matches</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[
                      { range: "70-80%", count: analytics?.match_density?.["70-80"] || 0 },
                      { range: "80-90%", count: analytics?.match_density?.["80-90"] || 0 },
                      { range: "90-100%", count: analytics?.match_density?.["90-100"] || 0 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#ccd500" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3 sticky top-0 z-10 bg-slate-100 py-3 -mx-8 px-8 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-[#384451]">Mi Perfil de Agente</h2>
              <Button
                className="bg-primary text-[#384451] hover:bg-[#b8c100] font-bold px-6"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/agent/profile`, {
                      agency_name: profile?.agency_name,
                      phone: profile?.phone,
                      bio: profile?.bio,
                      municipality: profile?.municipality,
                      authorized_region: profile?.authorized_region,
                      rfc: profile?.rfc,
                      certifications: profile?.certifications
                    });
                    toast.success("Perfil actualizado correctamente");
                  } catch (error) {
                    toast.error("Error al guardar cambios");
                  }
                }}
                data-testid="profile-save-top-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Guardar Cambios
              </Button>
            </div>

            {/* Master Partner Badge - If applicable */}
            {profile?.is_master_partner && (
              <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-800">Master Partner</h3>
                    <p className="text-sm text-amber-600">Región: {profile?.master_partner_region || profile?.authorized_region}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Profile Photo and Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Photo Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Foto de Perfil</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center">
                      {profile?.agent_photo ? (
                        <img src={profile.agent_photo} alt="Foto de perfil" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-16 h-16 text-slate-400" />
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 bg-primary text-[#384451] rounded-full p-2 cursor-pointer hover:bg-[#b8c100] transition-colors">
                      <Upload className="w-4 h-4" />
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            await axios.post(`${API}/agent/profile/upload-photo`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success("Foto actualizada");
                            fetchData();
                          } catch (error) {
                            toast.error("Error al subir foto");
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p className="mt-3 font-semibold text-lg">{profile?.name}</p>
                  <p className="text-sm text-slate-500">{profile?.email}</p>
                  
                  {/* Verification Status */}
                  <div className="mt-3 flex flex-col items-center gap-2">
                    {profile?.verified ? (
                      <Badge className="bg-green-500"><Shield className="w-3 h-3 mr-1" /> Agente Verificado</Badge>
                    ) : (
                      <Badge variant="secondary">Pendiente de Verificación</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Agency Info */}
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Información de Agencia</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nombre de Agencia</Label>
                      <Input 
                        value={profile?.agency_name || ""} 
                        onChange={(e) => setProfile({...profile, agency_name: e.target.value})}
                        placeholder="Mi Agencia Inmobiliaria" 
                      />
                    </div>
                    <div>
                      <Label>Teléfono / WhatsApp</Label>
                      <Input 
                        value={profile?.phone || ""} 
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        placeholder="+52 55 1234 5678" 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Bio Profesional</Label>
                    <Textarea 
                      value={profile?.bio || ""} 
                      onChange={(e) => setProfile({...profile, bio: e.target.value})}
                      placeholder="Describe tu experiencia y especialización en el sector inmobiliario..." 
                      rows={3} 
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Territorio Principal</Label>
                      <Select 
                        value={profile?.municipality || ""} 
                        onValueChange={(value) => setProfile({...profile, municipality: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona tu zona" />
                        </SelectTrigger>
                        <SelectContent>
                          {MUNICIPALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {profile?.municipality && !profile?.municipality_approved && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Pendiente de aprobación
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Región Autorizada</Label>
                      <Input 
                        value={profile?.authorized_region || ""} 
                        onChange={(e) => setProfile({...profile, authorized_region: e.target.value})}
                        placeholder="Ej: CDMX, Zona Metropolitana" 
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Fiscal & Verification Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Documentos Fiscales y Verificación
                </CardTitle>
                <CardDescription>
                  Estos documentos son necesarios para verificar tu identidad y activar funciones avanzadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* RFC */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Building className="w-4 h-4" /> RFC (Registro Federal de Contribuyentes)
                    </Label>
                    <Input 
                      value={profile?.rfc || ""} 
                      onChange={(e) => setProfile({...profile, rfc: e.target.value.toUpperCase()})}
                      placeholder="XXXX000000XXX" 
                      maxLength={13}
                      className="uppercase"
                    />
                    <p className="text-xs text-slate-500">Ingresa tu RFC con homoclave (13 caracteres)</p>
                  </div>

                  {/* INE */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4" /> INE (Identificación Oficial)
                    </Label>
                    {profile?.ine ? (
                      <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-700">INE Cargado</p>
                          <p className="text-xs text-green-600">Documento subido correctamente</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(profile.ine, '_blank')}
                        >
                          Ver
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                        <label className="cursor-pointer">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Sube tu INE (imagen o PDF)</p>
                          <p className="text-xs text-slate-400 mt-1">Máximo 5MB</p>
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*,.pdf"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error("El archivo es muy grande (máx 5MB)");
                                return;
                              }
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                await axios.post(`${API}/agent/profile/upload-ine`, formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                toast.success("INE subido correctamente");
                                fetchData();
                              } catch (error) {
                                toast.error("Error al subir INE");
                              }
                            }}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certifications & Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" /> Certificaciones y Especialidades
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(profile?.certifications || []).map((cert, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary" 
                      className="px-3 py-1 text-sm flex items-center gap-2"
                    >
                      {cert}
                      <button 
                        onClick={() => {
                          const newCerts = profile.certifications.filter((_, i) => i !== idx);
                          setProfile({...profile, certifications: newCerts});
                        }}
                        className="hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const cert = prompt("Nombre de la certificación:");
                      if (cert) {
                        setProfile({
                          ...profile, 
                          certifications: [...(profile?.certifications || []), cert]
                        });
                      }
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Agregar Certificación
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                className="bg-primary text-[#384451] px-8"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/agent/profile`, {
                      agency_name: profile?.agency_name,
                      phone: profile?.phone,
                      bio: profile?.bio,
                      municipality: profile?.municipality,
                      authorized_region: profile?.authorized_region,
                      rfc: profile?.rfc,
                      certifications: profile?.certifications
                    });
                    toast.success("Perfil actualizado correctamente");
                  } catch (error) {
                    toast.error("Error al guardar cambios");
                  }
                }}
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Guardar Cambios
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Membership Dialog */}
      <Dialog open={showMembership} onOpenChange={setShowMembership}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Planes de Membresía</DialogTitle>
            <DialogDescription>Selecciona el plan que mejor se adapte a tus necesidades</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {Object.entries(PLANS).filter(([k]) => k !== "freemium").map(([key, plan]) => {
              const periodLabel = key === "monthly" ? "/mes" : key === "semester" ? "/6 meses" : "/año";
              return (
                <Card key={key} className={`cursor-pointer transition-all ${profile?.membership_plan === key ? "ring-2 ring-primary" : "hover:shadow-lg"}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <CardDescription className="text-lg font-bold text-slate-800">
                      ${plan.price.toLocaleString()} MXN{periodLabel}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold text-primary mb-1">{plan.properties}</p>
                    <p className="text-sm text-slate-500 mb-2">propiedades activas</p>
                    <p className="text-xs text-slate-400 mb-4">{plan.description}</p>
                    <div className="space-y-2">
                      <Button className="w-full bg-primary text-[#384451]" size="sm" onClick={() => handleMembershipPurchase(key, "stripe")}>
                        <CreditCard className="w-4 h-4 mr-2" /> Pagar con Stripe
                      </Button>
                      <Button variant="outline" className="w-full" size="sm" onClick={() => handleMembershipPurchase(key, "transfer")}>
                        Pago por Transferencia
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Bank Transfer Info */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Datos para Transferencia Bancaria:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Banco:</span> Banregio</div>
              <div><span className="text-slate-500">CLABE:</span> 058597000024756989</div>
              <div className="col-span-2"><span className="text-slate-500">Beneficiario:</span> Leadsademy SAS de CV</div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Los pagos por transferencia quedan pendientes hasta verificación por el administrador.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Gap Dialog */}
      <Dialog open={showMatchGap} onOpenChange={setShowMatchGap}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Análisis de Brecha (Match Gap)</DialogTitle>
          </DialogHeader>
          {selectedProperty && <MatchGapAnalysis propertyId={selectedProperty} onEditProperty={openPropertyEdit} />}
        </DialogContent>
      </Dialog>

      {/* RIC Report Dialog */}
      <Dialog open={showRIC} onOpenChange={setShowRIC}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reporte de Inteligencia Comercial (RIC)</DialogTitle>
          </DialogHeader>
          {selectedProperty && <RICReport propertyId={selectedProperty} onClose={() => setShowRIC(false)} onEditProperty={openPropertyEdit} />}
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={showImageUpload} onOpenChange={setShowImageUpload}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gestionar Fotos</DialogTitle>
            <DialogDescription>
              Sube hasta 10 fotos. Las imágenes se comprimen automáticamente a ≤150KB. Arrastra para reordenar; la primera es la portada.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {selectedProperty && (
              <PhotoManager
                propertyId={selectedProperty}
                images={properties.find(p => p.id === selectedProperty)?.images || []}
                onChange={(newImages) => {
                  // Update local state immediately for snappy UI
                  setProperties(prev => prev.map(p => p.id === selectedProperty ? { ...p, images: newImages } : p));
                  // Background sync from server to refresh derived data
                  fetchData();
                }}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImageUpload(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={showEditProperty} onOpenChange={setShowEditProperty}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Propiedad</DialogTitle>
            <DialogDescription>
              Actualiza los datos de tu propiedad
            </DialogDescription>
          </DialogHeader>
          
          {editPropertyData && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Título</Label>
                <Input
                  id="edit-title"
                  value={editPropertyData.title}
                  onChange={(e) => setEditPropertyData({...editPropertyData, title: e.target.value})}
                  placeholder="Ej: Loft Industrial en Roma Norte"
                  data-testid="edit-property-title"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-story">Historia / Descripción</Label>
                <Textarea
                  id="edit-story"
                  value={editPropertyData.story}
                  onChange={(e) => setEditPropertyData({...editPropertyData, story: e.target.value})}
                  placeholder="Describe la propiedad..."
                  rows={4}
                  data-testid="edit-property-story"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-municipality">Municipio/Colonia</Label>
                <Select 
                  value={editPropertyData.municipality} 
                  onValueChange={(v) => setEditPropertyData({...editPropertyData, municipality: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {MUNICIPALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Recámaras</Label>
                  <Input 
                    type="number" 
                    value={editPropertyData.features.bedrooms} 
                    onChange={(e) => setEditPropertyData({
                      ...editPropertyData, 
                      features: {...editPropertyData.features, bedrooms: parseInt(e.target.value) || 0}
                    })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Baños</Label>
                  <Input 
                    type="number" 
                    step="0.5"
                    value={editPropertyData.features.bathrooms} 
                    onChange={(e) => setEditPropertyData({
                      ...editPropertyData, 
                      features: {...editPropertyData.features, bathrooms: parseFloat(e.target.value) || 0}
                    })} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terreno (m²)</Label>
                  <Input 
                    type="number" 
                    value={editPropertyData.features.land_area_m2 || editPropertyData.features.area || 0} 
                    onChange={(e) => setEditPropertyData({
                      ...editPropertyData, 
                      features: {...editPropertyData.features, land_area_m2: parseInt(e.target.value) || 0}
                    })} 
                    data-testid="edit-land-area"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Construido (m²)</Label>
                  <Input 
                    type="number" 
                    value={editPropertyData.features.construction_area_m2 || 0} 
                    onChange={(e) => setEditPropertyData({
                      ...editPropertyData, 
                      features: {...editPropertyData.features, construction_area_m2: parseInt(e.target.value) || 0}
                    })} 
                    data-testid="edit-construction-area"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio (MXN)</Label>
                  <Input 
                    type="number" 
                    value={editPropertyData.features.price} 
                    onChange={(e) => setEditPropertyData({
                      ...editPropertyData, 
                      features: {...editPropertyData.features, price: parseInt(e.target.value) || 0}
                    })} 
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditProperty(false); setEditPropertyData(null); }}>Cancelar</Button>
            <Button 
              onClick={savePropertyEdits} 
              disabled={savingEdit} 
              className="bg-primary text-[#384451]"
              data-testid="save-property-edit"
            >
              {savingEdit ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        open={showPropertyDetailModal}
        onOpenChange={(open) => {
          setShowPropertyDetailModal(open);
          if (!open) {
            setPropertyDetailInitialEditMode(false);
            setPropertyDetailInitialEditTab("info");
          }
        }}
        property={propertyDetailData?.property}
        statistics={propertyDetailData?.statistics}
        canEdit={true}
        canDelete={true}
        initialEditMode={propertyDetailInitialEditMode}
        initialEditTab={propertyDetailInitialEditTab}
        onDelete={async (id) => {
          await handleDeleteProperty(id);
          setShowPropertyDetailModal(false);
        }}
        onUpdate={() => {
          fetchData();
        }}
      />
    </div>
  );
};

export default AgentDashboard;
